// DayNotesSection — per-day notes panel above the bookings table.
//
// One card per note. Alert-type cards use the danger token (red bg + white
// text); standard "note" cards use the page-surface token. Each card shows
// the body, the staff name + datetime, and edit/delete affordances.
//
// Add / edit are delegated to the Add/EditDayNoteModal components.
// Delete is confirmed inline through the existing ConfirmModal and calls
// useDayNotes().deleteNote.

import { useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  Pencil,
  Plus,
  StickyNote,
  Trash2,
} from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { useToast } from '../../../hooks/useToast';
import { cn } from '../../../utils/cn';
import { AddDayNoteModal } from './AddDayNoteModal';
import { EditDayNoteModal } from './EditDayNoteModal';
import { useDayNotes } from '../hooks/useDayNotes';
import type { DiaryNoteDto } from '../../../types/api';
import styles from './DayNotesSection.module.css';

export interface DayNotesSectionProps {
  driverId: number;
  weekStart: string;
  date: string; // YYYY-MM-DD
  dayOfWeek: string;
  notes: DiaryNoteDto[];
}

function formatAdded(by: string | null, iso: string): string {
  const dt = new Date(iso);
  const dateStr = Number.isNaN(dt.getTime())
    ? iso
    : dt.toLocaleString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
  const who = by ?? 'Unknown';
  return `Added by ${who} on ${dateStr}`;
}

export function DayNotesSection({
  driverId,
  weekStart,
  date,
  dayOfWeek,
  notes,
}: DayNotesSectionProps): JSX.Element {
  const toast = useToast();
  const { deleteNote } = useDayNotes({ driverId, weekStart });

  const [addOpen, setAddOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<DiaryNoteDto | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DiaryNoteDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await deleteNote.mutateAsync(pendingDelete.enc_id);
      toast.success('Note deleted');
      setPendingDelete(null);
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not delete the note.'
        : 'Could not delete the note.';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <section
      className={styles.section}
      aria-labelledby={`day-notes-heading-${date}`}
    >
      <header className={styles.head}>
        <h3 className={styles.heading} id={`day-notes-heading-${date}`}>
          <StickyNote size={16} aria-hidden />
          Day Notes — {dayOfWeek}
        </h3>
        <span className={styles.subheading}>
          {notes.length === 0
            ? 'no notes'
            : `${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`}
        </span>
      </header>

      {notes.length === 0 ? (
        <div className={styles.empty} role="status">
          <span>No notes for this day</span>
          <Button
            variant="ghost"
            size="sm"
            icon={Plus}
            onClick={() => setAddOpen(true)}
            data-print-hide
          >
            Add Note
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.cards}>
            {notes.map((note) => {
              const isAlert = note.note_type === 'alert';
              return (
                <article
                  key={note.enc_id}
                  className={cn(styles.card, isAlert && styles.cardAlert)}
                >
                  {/* Card lays out as a single horizontal row:
                        [type badge] [note text] [staff·datetime] [actions]
                      Wrapping is allowed when the viewport is too narrow,
                      but the body's flex-basis keeps the row on one line
                      on any normal desktop width. */}
                  <span className={styles.typeBadge}>
                    {isAlert ? (
                      <>
                        <AlertTriangle size={11} aria-hidden /> Alert
                      </>
                    ) : (
                      'Note'
                    )}
                  </span>
                  <div className={styles.body}>{note.note_text}</div>
                  <span className={styles.meta}>
                    {formatAdded(note.added_by_name, note.added_at)}
                    {note.edited_at ? ' (edited)' : ''}
                  </span>
                  <span className={styles.actions} data-print-hide>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label="Edit note"
                      onClick={() => setEditingNote(note)}
                    >
                      <Pencil size={14} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      aria-label="Delete note"
                      onClick={() => setPendingDelete(note)}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </span>
                </article>
              );
            })}
          </div>
          <div className={styles.addRow} data-print-hide>
            <Button
              variant="secondary"
              size="sm"
              icon={Plus}
              onClick={() => setAddOpen(true)}
            >
              Add Note
            </Button>
          </div>
        </>
      )}

      <AddDayNoteModal
        open={addOpen}
        driverId={driverId}
        weekStart={weekStart}
        date={date}
        onClose={() => setAddOpen(false)}
      />

      <EditDayNoteModal
        open={editingNote !== null}
        driverId={driverId}
        weekStart={weekStart}
        note={editingNote}
        onClose={() => setEditingNote(null)}
      />

      <ConfirmModal
        open={pendingDelete !== null}
        title="Delete this note?"
        message="This cannot be undone."
        variant="danger"
        confirmLabel="Delete"
        cancelLabel="Cancel"
        loading={deleting}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </section>
  );
}
